import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Button, Spin } from 'antd';
import ToggleSwitch from '../../../../components/elements/toggleSwitch/ToggleSwitch';
import { toast } from 'react-toastify';
import ContentLayout from '../../../../layout/ContentLayout';
import { Container } from '../../../../components/content';
import { FloatingLabelInput } from '../../../../components/elements/floatingLabelInput/FloatingLabelInput';
import protocolSubcategoryApi from '../../../../api/protocol-subcategory-api';
import { useProtocolCategories } from '../../../../hooks/useProtocolCategories';
import { tabForSubcategoryCategory } from '../../../../utils/protocol-form.util';
import TranslateButton from '../../../../components/elements/TranslateButton/TranslateButton';
import { useTranslateFields } from '../../../../hooks/useTranslateFields';
import styles from '../../../../styles/mobileInfoPage.module.scss';

const LANG_SECTIONS = [
  { key: 'sr', label: 'Srpski' },
  { key: 'hu', label: 'Mađarski' },
  { key: 'en', label: 'Engleski' },
];

// The item's category is shown but not editable: protocols hold at most one
// item per category, so moving it could break that on protocols using it.
export default function IzmeniPodkategoriju() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [form, setForm] = useState(null);
  const [category, setCategory] = useState(null);
  const categoryOptions = useProtocolCategories();
  const categoryLabel = categoryOptions.find((c) => c.value === category)?.label || '';
  const backTo = `/protokoli?tab=${tabForSubcategoryCategory(category)}`;
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchDisease = useCallback(async () => {
    setLoading(true);
    const { data, success, message } = await protocolSubcategoryApi.getById(id);
    if (success && data) {
      setCategory(data.category);
      setForm({
        name: data.name || { sr: '', hu: '', en: '' },
        description: data.description || { sr: '', hu: '', en: '' },
        isActive: data.isActive,
      });
    } else {
      toast.error(message || 'Greška pri učitavanju stavke');
      navigate('/protokoli?tab=protokoli');
    }
    setLoading(false);
  }, [id, navigate]);

  useEffect(() => {
    fetchDisease();
  }, [fetchDisease]);

  const setName = (lang, val) =>
    setForm((prev) => ({ ...prev, name: { ...prev.name, [lang]: val } }));

  const setDescription = (lang, val) =>
    setForm((prev) => ({ ...prev, description: { ...prev.description, [lang]: val } }));

  const validate = () => {
    for (const { key, label } of LANG_SECTIONS) {
      if (!form.name[key]?.trim()) {
        toast.error(`Naziv na ${label}om je obavezan`);
        return false;
      }
    }
    return true;
  };

  const { translating, translate } = useTranslateFields();

  const handleTranslate = async () => {
    if (!form.name.sr?.trim()) {
      toast.error('Popunite naziv na srpskom pre prevođenja');
      return;
    }

    const fields = { name: form.name.sr };
    if (form.description.sr?.trim()) {
      fields.description = form.description.sr;
    }

    const result = await translate(fields);
    if (!result) return;

    for (const lang of ['hu', 'en']) {
      setName(lang, result[lang].name);
      if (result[lang].description !== undefined) {
        setDescription(lang, result[lang].description);
      }
    }

    toast.success('Prevod je uspešno primenjen');
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);

    const payload = {
      name: form.name,
      description: form.description,
      isActive: form.isActive,
    };

    const { success, message } = await protocolSubcategoryApi.update(id, payload);
    if (success) {
      toast.success('Stavka izmenjena');
      navigate(backTo);
    } else {
      toast.error(message || 'Greška pri izmeni stavke');
    }
    setSaving(false);
  };

  if (loading || !form) {
    return (
      <Container>
        <ContentLayout>
          <div className={styles.loadingContainer}>
            <Spin size="large" />
          </div>
        </ContentLayout>
      </Container>
    );
  }

  return (
    <Container>
      <Link to={backTo} className="link_btn" style={{ marginBottom: '12px' }}>
        <img alt="back" src="/assets/icons/chevron.svg" />
        <span>Nazad</span>
      </Link>

      <ContentLayout pt={true}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '32px',
          }}
        >
          <h2 className={styles.pageTitle} style={{ margin: 0 }}>
            Izmeni stavku{categoryLabel && ` — ${categoryLabel}`}
          </h2>
          <Button className="btn-primary" onClick={handleSave} loading={saving}>
            Sačuvaj izmene
          </Button>
        </div>

        <section className={styles.section}>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px' }}>
            <TranslateButton onClick={handleTranslate} loading={translating} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '32px' }}>
            <div>
              <h3 className={styles.sectionTitle}>Naziv</h3>
              <div className={styles.fieldStack}>
                {LANG_SECTIONS.map(({ key, label }) => (
                  <FloatingLabelInput
                    key={key}
                    label={`Naziv — ${label}`}
                    value={form.name[key]}
                    onChange={(e) => setName(key, e.target.value)}
                    placeholder={`Unesite naziv na ${label.toLowerCase()}om`}
                  />
                ))}
              </div>
            </div>
            <div>
              <h3 className={styles.sectionTitle}>Opis (opciono)</h3>
              <div className={styles.fieldStack}>
                {LANG_SECTIONS.map(({ key, label }) => (
                  <FloatingLabelInput
                    key={key}
                    label={`Opis — ${label}`}
                    value={form.description[key]}
                    onChange={(e) => setDescription(key, e.target.value)}
                    placeholder={`Unesite opis na ${label.toLowerCase()}om`}
                  />
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className={styles.section}>
          <ToggleSwitch
            checked={form.isActive}
            onChange={(e) => setForm((prev) => ({ ...prev, isActive: e.target.checked }))}
            label={form.isActive ? 'Aktivna' : 'Neaktivna'}
          />
        </section>
      </ContentLayout>
    </Container>
  );
}
